import React, { useState } from "react";
import { Download, Database } from "lucide-react";
import { UnitToggle, NumField, TextField, NotesField, Checkbox, u } from "./_shared";
import PullFromProfilesDrawer from "@/components/PullFromProfilesDrawer";

/**
 * WellProfileForm — depths, hole geometry, formation strength test, optional
 * subsea hookup (water depth, riser, choke line).
 *
 * P1 acceptance: "Pull values from existing profiles" auto-fills
 *   - lotTestMudDensity ← Mud Profile.density
 *   - shoeMd / shoeTvd  ← (none right now; placeholder for future Casing-as-source)
 */

const WELL_PULL_MAPPINGS = [
  { section: "Formation strength test (LOT/FIT)",
    field: "lotTestMudDensity", label: "Mud density at LOT", unit: "ppg / kg·m⁻³",
    sourceKind: "mud", sourceField: "density" },
];

export default function WellProfileForm({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const units = value.units || "IMP";
  const [pullOpen, setPullOpen] = useState(false);

  const handleApply = (patch, importedFrom) => {
    const next = { ...value, ...patch };
    next._importedFrom = { ...(value._importedFrom || {}), ...importedFrom };
    onChange(next);
    setPullOpen(false);
  };

  const importedKeys = Object.keys(value._importedFrom || {});

  return (
    <div className="flex flex-col gap-4" data-testid="well-profile-form">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[10.5px] text-[var(--rs-text-3)] leading-snug">
          Tip: tap <strong className="text-[var(--rs-amber)]">Pull values</strong>
          {" "}to copy the LOT-test mud density from any saved Mud Profile.
        </div>
        <button type="button"
                data-testid="well-pull-btn"
                onClick={() => setPullOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium whitespace-nowrap"
                style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 36 }}>
          <Download className="w-4 h-4" /> Pull values from existing profiles
        </button>
      </div>

      {importedKeys.length > 0 && (
        <div data-testid="well-import-audit"
             className="card-rs p-2 text-[10.5px]"
             style={{ borderColor: "rgba(245,180,0,0.45)", background: "rgba(245,180,0,0.05)" }}>
          <div className="font-mono uppercase tracking-[0.2em] text-[var(--rs-amber)] mb-1 inline-flex items-center gap-1">
            <Database className="w-3 h-3" /> Imported {importedKeys.length} value{importedKeys.length === 1 ? "" : "s"}
          </div>
          <div className="text-[var(--rs-text-2)] leading-snug">
            {importedKeys.map((k) => {
              const src = value._importedFrom[k];
              return `${k} ← ${src.kind}#${src.id}@v${src.version}`;
            }).join(" · ")}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <TextField
          value={value} onChange={onChange} field="label" label="Profile label"
          placeholder="e.g. Well X · Pad 7"
          testId="well-label"
        />
        <TextField
          value={value} onChange={onChange} field="rig" label="Rig (optional)"
          placeholder="e.g. Rig Alpha"
          testId="well-rig"
        />
        <UnitToggle value={units} onChange={(v) => set({ units: v })} testId="well-units" />
      </div>

      <fieldset className="border-t border-[var(--rs-border)] pt-3">
        <legend className="text-[10px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] px-2">
          Current depths
        </legend>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <NumField value={value} onChange={onChange} field="currentMd"  label="MD"          units={units} kind="depth" testId="well-currentMd" />
          <NumField value={value} onChange={onChange} field="currentTvd" label="TVD"         units={units} kind="depth" testId="well-currentTvd" />
          <NumField value={value} onChange={onChange} field="holeSize"   label="Hole size"   units={units} kind="dia"   testId="well-holeSize" />
          <div /> {/* spacer */}
          <NumField value={value} onChange={onChange} field="shoeMd"     label="Shoe MD"     units={units} kind="depth" testId="well-shoeMd" />
          <NumField value={value} onChange={onChange} field="shoeTvd"    label="Shoe TVD"    units={units} kind="depth" testId="well-shoeTvd" />
        </div>
      </fieldset>

      <fieldset className="border-t border-[var(--rs-border)] pt-3">
        <legend className="text-[10px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] px-2">
          Formation strength test (LOT / FIT)
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <NumField value={value} onChange={onChange} field="lotPressure"       label="Surface LOT pressure (A)" units={units} kind="press"   testId="well-lot" />
          <NumField value={value} onChange={onChange} field="lotTestMudDensity" label="Mud density at test (B)"  units={units} kind="density" testId="well-lotMw" />
        </div>
        <p className="text-[10px] text-[var(--rs-text-3)] mt-1">
          Used by the IWCF Kill Sheet MAASP chain. Optional today; required for
          MAASP calculation at kill-sheet time.
        </p>
      </fieldset>

      <fieldset className="border-t border-[var(--rs-border)] pt-3">
        <legend className="text-[10px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] px-2">
          Subsea hookup (optional)
        </legend>
        <Checkbox
          checked={value.isSubsea}
          onChange={(b) => set({ isSubsea: b })}
          label="This well uses a subsea BOP stack"
          testId="well-isSubsea"
        />
        {value.isSubsea && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
            <NumField value={value} onChange={onChange} field="waterDepth"      label="Water depth"       units={units} kind="depth"  testId="well-waterDepth" />
            <NumField value={value} onChange={onChange} field="riserIdIn"       label="Riser ID"          units={units} kind="dia"    testId="well-riserId" />
            <NumField value={value} onChange={onChange} field="riserCapacity"   label="Riser capacity"    units={units} kind="cap"    testId="well-riserCap" />
            <NumField value={value} onChange={onChange} field="chokeLineLength" label="Choke-line length" units={units} kind="depth"  testId="well-chokeLen" />
            <NumField value={value} onChange={onChange} field="chokeLineIdIn"   label="Choke-line ID"     units={units} kind="dia"    testId="well-chokeId" />
          </div>
        )}
      </fieldset>

      <NotesField value={value} onChange={onChange} testId="well-notes" />

      <PullFromProfilesDrawer
        open={pullOpen}
        title="Pull values into Well Profile"
        target={value}
        mappings={WELL_PULL_MAPPINGS}
        onApply={handleApply}
        onClose={() => setPullOpen(false)}
      />
    </div>
  );
}
